import { ID } from '@griply/types';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { ConnectionCalendar } from 'shared/hooks/queries/use-external-connections-calendars-queries';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import * as Styled from './calendar-sync-options.style';

export type CalendarSelectValue = { connectionId: ID; calendarId: ID };

export type CalendarSelectProps = {
  calendars: ConnectionCalendar[];
  value: CalendarSelectValue | null;
  onChange: (value: CalendarSelectValue) => void;
};

export const CalendarSelect: React.FC<CalendarSelectProps> = ({
  calendars,
  value,
  onChange,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { menuOpen, openMenu, closeMenu } = useOpenMenu();

  const selected = useMemo(
    () =>
      calendars.find(
        (calendar) =>
          calendar.id === value?.calendarId &&
          calendar.connectionId === value?.connectionId,
      ),
    [calendars, value],
  );

  return (
    <>
      <Styled.SelectButton ref={buttonRef} type="button" onClick={openMenu}>
        {selected ? (
          <Styled.SelectValue>
            <Styled.CalendarColor $color={selected.color} />
            <Styled.SelectLabel>{selected.name}</Styled.SelectLabel>
          </Styled.SelectValue>
        ) : (
          <Styled.Placeholder>
            {t('forms.user-settings.sections.calendar.sync-tasks.placeholder')}
          </Styled.Placeholder>
        )}
        <Styled.ChevronContainer>
          <Icon icon={ChevronLeft} />
        </Styled.ChevronContainer>
      </Styled.SelectButton>

      {menuOpen && (
        <PopupMenu
          referenceElement={buttonRef}
          onOutside={closeMenu}
          position="bottom-start"
        >
          <PopupMenuList>
            {calendars.map((calendar) => {
              const onSelect = () => {
                onChange({
                  connectionId: calendar.connectionId,
                  calendarId: calendar.id,
                });
                closeMenu();
              };
              return (
                <PopupMenuListItem
                  key={`${calendar.connectionId}:${calendar.id}`}
                >
                  <PopupMenuButton
                    onClick={onSelect}
                    start={<Styled.CalendarColor $color={calendar.color} />}
                  >
                    {calendar.name}
                  </PopupMenuButton>
                </PopupMenuListItem>
              );
            })}
          </PopupMenuList>
        </PopupMenu>
      )}
    </>
  );
};
